import { useCallback, useEffect, useRef, useState } from 'react';
import { looksJammed } from '../geo';
import { requestRoute } from './client';
import { flatMetres } from './snap';
import { useOfflinePointers } from './offlineData';

/*
 * The route to the active target from the phone's own fix, kept current.
 *
 * Asked when the target or the travel mode changes (once a fix is there),
 * and again when the person has left the route: OFF_ROUTE_FIXES fixes in a
 * row farther than max(OFF_ROUTE_M, twice the fix's accuracy) from it, at
 * most once every RECALC_MS. A fix that looks jammed (utils/geo.looksJammed)
 * is never routed from. While a new route is being found the old one stays
 * drawn.
 *
 * status: 'idle' (no target) | 'waiting' (no fix yet) | 'loading' | 'ok' |
 * 'no_route' (none in the data: the straight line stays) | 'unavailable'
 * (no routing data reachable, online or kept) | 'error'
 */
export const OFF_ROUTE_M = 40;
export const OFF_ROUTE_FIXES = 3;
export const RECALC_MS = 30_000;

function segmentMetres(lat, lng, [la1, lo1], [la2, lo2]) {
  const k = Math.cos((lat * Math.PI) / 180);
  const ax = (lo1 - lng) * k;
  const ay = la1 - lat;
  const dx = (lo2 - lo1) * k;
  const dy = la2 - la1;
  const seg = dx * dx + dy * dy;
  const t = seg === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / seg));
  return flatMetres(lat, lng, la1 + (la2 - la1) * t, lo1 + (lo2 - lo1) * t);
}

/** How far a point is from the nearest part of a route, metres. */
export function distanceToRoute(route, lat, lng) {
  let best = Infinity;
  for (const leg of [route?.approach, route?.drive, route?.walk, route?.offTrail]) {
    const pts = leg?.points || [];
    for (let i = 1; i < pts.length; i += 1) best = Math.min(best, segmentMetres(lat, lng, pts[i - 1], pts[i]));
  }
  return best;
}

export default function useRoute({ target, fix, mode, enabled = true, request = requestRoute, now = Date.now }) {
  const pointers = useOfflinePointers(enabled && !!target);
  const pointerUrl = pointers?.routing?.pointerUrl || null;
  const [state, setState] = useState({ status: 'idle', route: null, attribution: null });
  const asked = useRef({ key: '', at: 0 });
  const offCount = useRef(0);
  const current = useRef(null);
  const seq = useRef(0);
  const key = enabled && target ? `${target.lat},${target.lng},${mode}` : '';
  const fixLat = fix?.lat;
  const fixLng = fix?.lng;
  const accuracy = Number(fix?.accuracy_m) || 0;
  const usable = Number.isFinite(fixLat) && Number.isFinite(fixLng) && !looksJammed(fixLat, fixLng);

  const ask = useCallback((start) => {
    seq.current += 1;
    const mine = seq.current;
    asked.current = { key, at: now() };
    offCount.current = 0;
    setState((s) => ({ ...s, status: 'loading' }));
    request({ pointerUrl, start, target: { lat: target.lat, lng: target.lng }, mode }).then((answer) => {
      if (mine !== seq.current) return;
      if (answer?.status === 'ok') {
        current.current = answer.route;
        setState({ status: 'ok', route: answer.route, attribution: answer.attribution || null });
      } else {
        current.current = null;
        setState({ status: answer?.status || 'error', route: null, attribution: answer?.attribution || null });
      }
    });
  }, [key, mode, now, pointerUrl, request, target]);

  useEffect(() => {
    if (!key) {
      seq.current += 1;
      current.current = null;
      asked.current = { key: '', at: 0 };
      setState({ status: 'idle', route: null, attribution: null });
      return;
    }
    if (!pointerUrl) {
      setState({ status: 'unavailable', route: null, attribution: null });
      return;
    }
    if (!usable) {
      if (asked.current.key !== key) setState({ status: 'waiting', route: null, attribution: null });
      return;
    }
    const start = { lat: fixLat, lng: fixLng };
    if (asked.current.key !== key) {
      current.current = null;
      ask(start);
      return;
    }
    if (!current.current) return;
    const away = distanceToRoute(current.current, fixLat, fixLng);
    offCount.current = away > Math.max(OFF_ROUTE_M, 2 * accuracy) ? offCount.current + 1 : 0;
    if (offCount.current >= OFF_ROUTE_FIXES && now() - asked.current.at >= RECALC_MS) ask(start);
  }, [key, pointerUrl, usable, fixLat, fixLng, accuracy, ask, now]);

  return state;
}
